/**
 * Slide Randomizer
 * 
 * Utility class for shuffling DOM elements using the Fisher-Yates algorithm.
 * Provides a clean interface for randomizing slide order without modifying
 * the original DOM structure until the shuffle is applied.
 */
export class SlideRandomizer {
    
    /**
     * Shuffles an array of DOM elements using Fisher-Yates algorithm
     * @param {HTMLElement[]} elements - Array of DOM elements to shuffle
     * @returns {HTMLElement[]} New array with shuffled elements (original array unchanged)
     */
    static randomizeElements(elements) {
        if (!elements || elements.length <= 1) {
            return elements ? [...elements] : [];
        }
        
        // Create a copy to avoid modifying the original array
        const shuffled = [...elements];
        
        // Fisher-Yates shuffle
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        
        return shuffled;
    }
    
    /**
     * Applies a shuffled order to DOM elements in their parent container
     * @param {HTMLElement[]} originalElements - Original elements in current order
     * @param {HTMLElement[]} shuffledElements - Elements in desired shuffled order
     * @param {HTMLElement} container - Parent container to reorder elements within
     */
    static applyShuffledOrder(originalElements, shuffledElements, container) {
        if (!originalElements || !shuffledElements || !container) {
            console.warn('[SlideRandomizer] Missing required parameters for applyShuffledOrder');
            return;
        }
        
        if (originalElements.length !== shuffledElements.length) {
            console.warn('[SlideRandomizer] Element arrays must be the same length');
            return;
        }
        
        // Remove all elements from container (but keep them in memory)
        const detachedElements = shuffledElements.map(element => {
            container.removeChild(element);
            return element;
        });
        
        // Re-append in shuffled order
        detachedElements.forEach(element => {
            container.appendChild(element);
        });
    }
    
    /**
     * Convenience method to randomize slides within a carousel viewport
     * @param {HTMLElement} viewport - The carousel viewport containing slide elements
     * @param {string} slideSelector - CSS selector for slide elements (e.g., '.carousel__slide')
     * @returns {HTMLElement[]} Array of slide elements in their new shuffled order
     */
    static randomizeSlides(viewport, slideSelector = '.carousel__slide') {
        if (!viewport) {
            console.warn('[SlideRandomizer] No viewport provided');
            return [];
        }
        
        const slides = Array.from(viewport.querySelectorAll(slideSelector));
        
        if (slides.length <= 1) {
            return slides;
        }
        
        const shuffledSlides = SlideRandomizer.randomizeElements(slides);
        SlideRandomizer.applyShuffledOrder(slides, shuffledSlides, viewport);
        
        console.log('[SlideRandomizer] Randomized', shuffledSlides.length, 'slides');
        return shuffledSlides;
    }
}